"use client";

import React from "react";
import { Calendar, User } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function MainPostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto animate-pulse">
        <div className="h-12 bg-muted rounded-md w-3/4 mx-auto mb-6"></div>
        <div className="h-6 bg-muted rounded-md w-1/2 mx-auto mb-12"></div>
        <div className="h-[400px] bg-muted rounded-2xl mb-12"></div>
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded w-full"></div>
          <div className="h-4 bg-muted rounded w-full"></div>
          <div className="h-4 bg-muted rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-destructive font-medium">콘텐츠를 불러오는 중 오류가 발생했습니다.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-muted-foreground text-lg">게시글을 찾을 수 없습니다.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto">
      <header className="mb-12 text-center">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold text-foreground mb-8 leading-tight tracking-tight">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground text-sm">
          {post.date && (
            <div className="flex items-center gap-2 bg-secondary/50 px-4 py-2 rounded-lg">
              <Calendar className="h-4 w-4 text-primary" />
              <time dateTime={post.date} className="font-medium">
                {new Date(post.date).toLocaleDateString('ko-KR', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-16 rounded-lg overflow-hidden shadow-xl border border-border/50 bg-muted">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover max-h-[700px]"
            loading="lazy"
          />
        </div>
      )}

      <div className="bg-card rounded-lg p-8 md:p-12 shadow-sm border border-border/50">
        <div
          className="prose prose-lg max-w-none text-foreground/90 leading-relaxed [&>h2]:font-default [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-foreground [&>h3]:font-default [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:mt-8 [&>h3]:mb-4 [&>h3]:text-foreground [&>p]:mb-6 [&>p]:text-lg font-default [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2 [&>img]:rounded-lg [&>img]:my-10 [&>img]:shadow-md [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:my-8 [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80"
          dangerouslySetInnerHTML={{ __html: post.content || '' }}
        />
      </div>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mt-24 pt-16 border-t border-border">
        <div className="h-8 bg-muted rounded w-64 mx-auto mb-12 animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[1, 2, 3].map((i) => (
            <div key={i} data-index={i} className="h-96 bg-muted rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="mt-24 pt-16 border-t border-border">
      <div className="text-center mb-12">
        <h3 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4">
          최신 소식
        </h3>
        <p className="text-muted-foreground max-w-2xl mx-auto font-default">
          건양사이버대학교의 최신 입시 정보와 학사 소식을 확인하세요.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {posts.map((post, i) => (
          <Card key={post.id} data-index={i} className="flex flex-col h-full overflow-hidden hover:shadow-lg transition-all duration-300 border-border/50 bg-card group rounded-lg">
            {post.featuredImage && (
              <Link to={post.link || "#"} className="block aspect-[4/3] overflow-hidden relative">
                <div className="absolute inset-0 bg-foreground/5 group-hover:bg-transparent transition-colors duration-300 z-10"></div>
                <img 
                  src={post.featuredImage} 
                  alt={post.title} 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                  loading="lazy"
                />
              </Link>
            )}
            <CardHeader className="pt-6">
              <Link to={post.link || "#"}>
                <CardTitle className="text-xl font-default font-bold hover:text-primary transition-colors line-clamp-2 leading-tight">
                  {post.title}
                </CardTitle>
              </Link>
            </CardHeader>
            <CardContent className="flex-grow">
              <p className="text-muted-foreground line-clamp-3 text-sm leading-relaxed font-default">
                {post.excerpt?.replace(/<[^>]*>?/gm, '') || "건양사이버대학교의 입시 정보와 학사 소식을 확인해 보세요."}
              </p>
            </CardContent>
            <CardFooter className="pb-6">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors border-primary text-primary font-default rounded-lg">
                  자세히 보기
                </Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && (window as any).wvcClient
    ? (window as any).wvcClient.getCurrentWPQueryParams()
    : { post_type: 'post', p: 1 }; 

  const wp_query = new WP_Query(currentWPQueryParams);

  
  const related_query_params = { 
    post_type: currentWPQueryParams.post_type || 'post', 
    per_page: 3,
    
    ...(currentWPQueryParams.p ? { post__not_in: [currentWPQueryParams.p] } : {})
  };
  const related_wp_query = new WP_Query(related_query_params);

  return (
    <section data-section-id="235577" id="single-post-template-section" className="bg-background py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <PostsProvider wp_query={wp_query}>
          <MainPostConsumer />
        </PostsProvider>

        <PostsProvider wp_query={related_wp_query}>
          <RelatedPostsConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}